import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import LegalFooter from "@/components/legal/LegalFooter";
import { ArrowLeft } from "lucide-react";

/**
 * Shared layout for /legal/* and /contact pages.
 *
 * Provides a calm reading column with a sticky right-rail Table of Contents
 * on desktop, a clean header with brand + back link, document-metadata band
 * (Effective Date / Last Updated), and the global LegalFooter.
 *
 * Section anchors are derived from each section's `id` prop and rendered as
 * a sticky TOC on `lg` screens. Smooth-scroll is applied so anchor jumps
 * feel native. The TOC collapses on mobile where it would compete with
 * the reading column.
 */
export default function LegalLayout({
  eyebrow,
  title,
  intro,
  effectiveDate = "January 1, 2026",
  lastUpdated = "January 1, 2026",
  toc,
  metaTitle,
  metaDescription,
  canonical,
  children,
  testid,
}) {
  useEffect(() => {
    if (metaTitle) document.title = metaTitle;
    if (metaDescription) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", "description");
        document.head.appendChild(m);
      }
      m.setAttribute("content", metaDescription);
    }
    if (canonical) {
      let l = document.querySelector('link[rel="canonical"]');
      if (!l) {
        l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        document.head.appendChild(l);
      }
      l.setAttribute("href", canonical);
    }
  }, [metaTitle, metaDescription, canonical]);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--fop-bg)", color: "var(--fop-text)" }}
      data-testid={testid}
    >
      <header
        className="border-b"
        style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface)" }}
      >
        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex" data-testid="legal-back-home">
            <Logo size="sm" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.22em]"
            style={{ color: "var(--fop-text-3)" }}
            data-testid="legal-home-link"
          >
            <ArrowLeft size={12} /> Home
          </Link>
        </div>
        <div className="h-1 industrial-stripe" />
      </header>

      <main className="flex-1">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="mb-10">
            {eyebrow && (
              <div
                className="font-mono text-[11px] uppercase tracking-[0.3em] mb-3"
                style={{ color: "var(--fop-primary)" }}
                data-testid="legal-eyebrow"
              >
                {eyebrow}
              </div>
            )}
            <h1
              className="font-display text-4xl sm:text-5xl leading-[1.05] mb-4"
              data-testid="legal-title"
            >
              {title}
            </h1>
            {intro && (
              <p
                className="text-base sm:text-lg max-w-3xl leading-relaxed"
                style={{ color: "var(--fop-text-2)" }}
              >
                {intro}
              </p>
            )}
            <div
              className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-[0.22em]"
              style={{ color: "var(--fop-text-3)" }}
            >
              <span data-testid="legal-effective-date">
                Effective Date · {effectiveDate}
              </span>
              <span aria-hidden="true" className="opacity-40">|</span>
              <span data-testid="legal-last-updated">
                Last Updated · {lastUpdated}
              </span>
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_240px] gap-10 lg:gap-14">
            <article
              className="legal-prose space-y-10 max-w-3xl"
              data-testid="legal-body"
            >
              {children}
            </article>

            {toc && toc.length > 0 && (
              <aside className="hidden lg:block">
                <div
                  className="sticky top-8 rounded-md border px-4 py-4"
                  style={{
                    borderColor: "var(--fop-border)",
                    background: "var(--fop-surface)",
                  }}
                  data-testid="legal-toc"
                >
                  <div
                    className="font-mono text-[10px] uppercase tracking-[0.25em] mb-3"
                    style={{ color: "var(--fop-text-3)" }}
                  >
                    On this page
                  </div>
                  <nav className="space-y-1.5 text-sm">
                    {toc.map((t) => (
                      <a
                        key={t.id}
                        href={`#${t.id}`}
                        className="block transition-colors"
                        style={{ color: "var(--fop-text-2)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--fop-text)")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--fop-text-2)")}
                      >
                        {t.label}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>
            )}
          </div>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}

/**
 * Section block — a single anchored chapter inside a legal page.
 * Renders an `<h2 id="...">` for deep-linking + sticky TOC scroll.
 */
export function LegalSection({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`legal-section-${id}`}>
      <h2
        className="font-display text-2xl sm:text-3xl mb-4 leading-tight"
        style={{ color: "var(--fop-text)" }}
      >
        {title}
      </h2>
      <div
        className="space-y-3 text-[15px] leading-relaxed"
        style={{ color: "var(--fop-text-2)" }}
      >
        {children}
      </div>
    </section>
  );
}

/**
 * Bullet list helper — keeps spacing/typography consistent across pages.
 */
export function LegalList({ items, testid }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5" data-testid={testid}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}
